import { auth, db } from "../lib/firebase";
import {
  get,
  ref,
  set,
  orderByChild,
  query,
  startAt,
  startAfter,
  limitToFirst,
  endAt,
  equalTo,
  update,
  push,
} from "firebase/database";
import { differenceInHours } from "date-fns";
import { isNetworkError, LaunchError } from "../utils/errors";
import {
  buildOwnerLoginAsLaunchErrorArgs,
  startRequestOwnerLoginAsClientLink,
} from "./ownerLoginAsClientLink.js";
import { encryptPassword } from "../utils/string";
import {
  ADS_CAMPAIGN_STATUS_ACTIVE,
  DATE_RANGE,
  GOOGLE,
  LINKEDIN,
  META,
  TIKTOK,
} from "../utils/constants";
import types from "../types";
import {
  CREDITS_NOT_BE_NEGATIVE,
  MISSING_PARAMETERS,
  WITH_OUT_PLAN,
} from "../i18n/keysTranslations";
import _ from "lodash";
import { startGetTotalStatisticsAllCampaigns } from "./adsCampaigns";
import { waitDelay } from "../utils/date";
import { getDateRangeFromPreset } from "../utils/dateRangePresets";
import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { renewToken } from "./auth";
import { getRedAlertsSummaryFromCampaigns } from "../utils/campaignAlerts";
import { fetchAlertItemStatuses } from "../utils/campaignAlertItemStatus";
import { formatHealthScoreIsPlanActive } from "../pages/AdminDashboard/utils/healthScoreIsPlanActiveComposite";
import { pickClosestSearchEmail } from "../pages/AdminDashboard/utils/searchMatchEmail";
import {
  FEATURE_FLAG_STORAGE,
  getFeatureFlagByKey,
} from "../pages/AdminDashboard/featureFlags";
import {
  DEFAULT_AGENCY_IMAGE_PROVIDER,
  isValidAgencyImageProvider,
  resolveAgencyImageProvider,
} from "../pages/AdminDashboard/imageProviders";
import { setAgenciesFeatureFlags } from "./agenciesFeatureFlags";
import { hasHiddenDefaultClientsListPlanPrice } from "../pages/AdminDashboard/utils/clientsListVisibility";
import { isClientTextSearch } from "../utils/clientTextSearch";
import { replaceUndefinedWithNull } from "../utils/forms";

/** Cursor for healthScoreIsPlanActive pagination: "{composite}::{advertiserKey}" */
const IS_PLAN_ACTIVE_PAGE_CURSOR_SEP = "::";

/**
 * Configuración de filtros para búsqueda de advertisers
 * Cada filtro define cómo construir queries de Firebase y extraer lastKey
 */
const FILTER_CONFIGS = {
  isPlanActive: {
    orderByField: "isPlanActive",
    buildQuery: (dbRef, filter, lastKey, limit) => {
      const base = query(dbRef, orderByChild("healthScoreIsPlanActive"));
      const filterEndFormatted = formatHealthScoreIsPlanActive({
        healthScore: Number(100),
        isPlanActive: filter,
      });
      if (lastKey) {
        let composite;
        let childKey = null;
        const sepIdx = lastKey.indexOf(IS_PLAN_ACTIVE_PAGE_CURSOR_SEP);
        composite = lastKey.slice(0, sepIdx);
        childKey = lastKey.slice(
          sepIdx + IS_PLAN_ACTIVE_PAGE_CURSOR_SEP.length
        );
        if (childKey) {
          return query(
            base,
            startAfter(composite, childKey),
            endAt(filterEndFormatted),
            limitToFirst(limit)
          );
        }
        return query(
          base,
          startAfter(composite),
          endAt(filterEndFormatted),
          limitToFirst(limit)
        );
      }
      const filterStartFormatted = formatHealthScoreIsPlanActive({
        healthScore: Number(0),
        isPlanActive: filter,
      });
      return query(
        base,
        startAt(filterStartFormatted),
        endAt(filterEndFormatted),
        limitToFirst(limit)
      );
    },
    extractLastKey: (advertisersKeys, advertisers) => {
      if (advertisersKeys.length === 0) return null;

      const advertiserKey = advertisersKeys[advertisersKeys.length - 1];
      const advertiserData = advertisers[advertiserKey];
      return advertiserData.healthScoreIsPlanActive;
    },
  },
  // Primary stream for client text search (business name prefix).
  businessNameLowercase: {
    orderByField: "businessNameLowercase",
    buildQuery: (dbRef, filter, lastKey, limit) => {
      const base = query(dbRef, orderByChild("businessNameLowercase"));
      if (lastKey) {
        return query(
          base,
          startAfter(lastKey),
          endAt(filter + "\uf8ff"),
          limitToFirst(limit)
        );
      } else {
        return query(
          base,
          startAt(filter),
          endAt(filter + "\uf8ff"),
          limitToFirst(limit)
        );
      }
    },
    extractLastKey: (advertisersKeys, advertisers) => {
      const lastAdvertiserID = advertisersKeys[advertisersKeys.length - 1];
      const lastAdvertiserData = advertisers[lastAdvertiserID];
      return lastAdvertiserData.businessNameLowercase || lastAdvertiserID;
    },
  },
  // Secondary stream for client text search: prefix query on adsUsersIndex.email.
  // Emails are stored lowercase (adsUsersIndex sync); search term is lowercased in useClientsList.
  adsUsersIndexEmail: {
    orderByField: "email",
    buildQuery: (dbRef, filter, lastKey, limit) => {
      const base = query(dbRef, orderByChild("email"));
      const endBound = `${filter}\uf8ff`;
      if (lastKey) {
        return query(
          base,
          startAfter(lastKey),
          endAt(endBound),
          limitToFirst(limit)
        );
      }
      return query(base, startAt(filter), endAt(endBound), limitToFirst(limit));
    },
    extractLastKey: (orderedKeys, entries) => {
      if (orderedKeys.length === 0) return null;
      const lastEntryKey = orderedKeys[orderedKeys.length - 1];
      const lastEntry = entries[lastEntryKey];
      return lastEntry?.email || lastEntryKey;
    },
  },
};

const isAdvertiserExcludedFromClientsList = (advertiserData = {}) =>
  advertiserData.isDemo === true ||
  advertiserData.isDeleted === true ||
  advertiserData.createdFrom === "wacm";

const isValidAgencyIDForSearch = (agencyID) =>
  agencyID && agencyID !== "unknown" && String(agencyID).trim() !== "";

const collectEmailIndexHitsFromSnapshot = (emailIndexSnapshot) => {
  const orderedKeys = [];
  const entries = {};
  const emailsByAgency = {};

  if (!emailIndexSnapshot) {
    return { orderedKeys, entries, emailsByAgency };
  }

  emailIndexSnapshot.forEach((child) => {
    orderedKeys.push(child.key);
    const entry = child.val() || {};
    entries[child.key] = entry;

    const { agencyID, email } = entry;
    if (!isValidAgencyIDForSearch(agencyID) || !email) {
      return;
    }

    if (!emailsByAgency[agencyID]) {
      emailsByAgency[agencyID] = [];
    }
    emailsByAgency[agencyID].push(email);
  });

  return { orderedKeys, entries, emailsByAgency };
};

const applySearchMatchedEmailsToValidData = ({
  validData,
  emailsByAgency,
  searchTerm,
}) => {
  if (!searchTerm || _.isEmpty(emailsByAgency) || _.isEmpty(validData)) {
    return;
  }

  Object.keys(validData).forEach((advertiserID) => {
    const advertiser = validData[advertiserID];
    const agencyID = advertiser?.agencyID;
    const agencyEmails = agencyID ? emailsByAgency[agencyID] : null;

    if (!agencyEmails?.length) {
      return;
    }

    const searchMatchedEmail = pickClosestSearchEmail({
      emails: agencyEmails,
      searchTerm,
    });

    if (searchMatchedEmail) {
      validData[advertiserID] = {
        ...advertiser,
        searchMatchedEmail,
      };
    }
  });
};

/**
 * Active plan filtering still needs a post-check because pending-payment rows
 * are merged separately into the active list.
 */
const matchesIsPlanActiveFilter = (advertiserData, filter, filterType) => {
  if (filterType !== "isPlanActive" || typeof filter !== "boolean") {
    return true;
  }
  if (filter === true) {
    return (
      advertiserData.isPlanActive === true ||
      Boolean(advertiserData?.isPendingPayment)
    );
  }
  return advertiserData.isPlanActive === filter;
};

const ADVERTISER_FULL_DATA_CACHE_HOURS = 24;

const KPI_SUPPORTED_PLATFORMS = [GOOGLE, META, TIKTOK, LINKEDIN];

const getUniqueStringValues = (values = []) => {
  const uniqueValues = [];
  const seen = new Set();

  values.forEach((value) => {
    if (typeof value !== "string") return;

    const valueTrimmed = value.trim();
    if (!valueTrimmed || seen.has(valueTrimmed)) return;

    seen.add(valueTrimmed);
    uniqueValues.push(valueTrimmed);
  });

  return uniqueValues;
};

const getGoogleCampaignKpis = (campaign = {}) => {
  const conversionActions =
    campaign?.platformsData?.[GOOGLE]?.conversionActions || [];

  return getUniqueStringValues(
    conversionActions.map((action) => action?.category).filter(Boolean)
  ).map((value) => ({
    platform: GOOGLE,
    value,
  }));
};

const getObjectiveCampaignKpis = ({ campaign = {}, platform }) => {
  const objective = campaign?.platformsData?.[platform]?.objective;
  const uniqueObjectives = getUniqueStringValues([objective]);

  return uniqueObjectives.map((value) => ({
    platform,
    value,
  }));
};

const getCampaignStatisticsCurrency = ({ campaign = {}, platform }) => {
  const currency = campaign?.statistics?.[platform]?.currency;
  return typeof currency === "string" && currency.trim()
    ? currency.trim()
    : null;
};

const getCampaignKpiEntries = (campaign = {}) => {
  const availablePlatforms = Array.isArray(campaign?.platforms)
    ? campaign.platforms.filter((platform) =>
        KPI_SUPPORTED_PLATFORMS.includes(platform)
      )
    : KPI_SUPPORTED_PLATFORMS.filter(
        (platform) => campaign?.platformsData?.[platform] != null
      );

  const entries = [];

  availablePlatforms.forEach((platform) => {
    if (platform === GOOGLE) {
      entries.push(...getGoogleCampaignKpis(campaign));
      return;
    }

    entries.push(getObjectiveCampaignKpis({ campaign, platform }));
  });

  return entries.flat();
};

const getCampaignTargetCpaEntries = ({ campaign = {}, adsGroupsByID = {} }) => {
  const entries = [];
  const campaignKpiEntries = getCampaignKpiEntries(campaign);

  const googleBiddingValue = Number(
    campaign?.platformsData?.[GOOGLE]?.biddingStrategy?.value
  );
  if (
    !Number.isNaN(googleBiddingValue) &&
    Number.isFinite(googleBiddingValue)
  ) {
    const currency = getCampaignStatisticsCurrency({
      campaign,
      platform: GOOGLE,
    });
    campaignKpiEntries
      .filter((entry) => entry.platform === GOOGLE)
      .forEach((entry) => {
        entries.push({
          ...entry,
          targetCpa: googleBiddingValue,
          currency,
        });
      });
  }

  const adGroupIDs = Object.keys(campaign?.adsGroups || {});
  adGroupIDs.forEach((adGroupID) => {
    const adGroup = adsGroupsByID[adGroupID];
    const platform = adGroup?.platform;
    const bidGoal = Number(adGroup?.bidGoal);

    if (![META, TIKTOK].includes(platform)) return;
    if (Number.isNaN(bidGoal) || !Number.isFinite(bidGoal)) return;

    const currency = getCampaignStatisticsCurrency({ campaign, platform });

    campaignKpiEntries
      .filter((entry) => entry.platform === platform)
      .forEach((entry) => {
        entries.push({
          ...entry,
          targetCpa: bidGoal,
          currency,
        });
      });
  });

  return entries;
};

const getAdvertiserKpiSummary = ({ campaigns = {}, adsGroupsByID = {} }) => {
  const kpiStats = new Map();
  const campaignTargetEntries = [];

  Object.values(campaigns || {}).forEach((campaign) => {
    const campaignKpiEntries = getCampaignKpiEntries(campaign);

    campaignKpiEntries.forEach((entry) => {
      const mapKey = `${entry.platform}::${entry.value}`;
      const existing = kpiStats.get(mapKey);

      if (existing) {
        existing.count += 1;
        return;
      }

      kpiStats.set(mapKey, {
        ...entry,
        count: 1,
      });
    });

    campaignTargetEntries.push(
      ...getCampaignTargetCpaEntries({ campaign, adsGroupsByID })
    );
  });

  const rankedKpis = Array.from(kpiStats.values());
  if (rankedKpis.length === 0) {
    return {
      primaryKpi: null,
      targetCpaParts: [],
    };
  }

  let primaryKpi = rankedKpis[0];
  rankedKpis.forEach((entry) => {
    if (entry.count > primaryKpi.count) {
      primaryKpi = entry;
    }
  });

  const matchingTargetEntries = campaignTargetEntries.filter(
    (entry) =>
      entry.platform === primaryKpi.platform && entry.value === primaryKpi.value
  );

  const numericEntries = matchingTargetEntries.filter(
    (entry) =>
      typeof entry.targetCpa === "number" && Number.isFinite(entry.targetCpa)
  );

  const byCurrency = new Map();
  numericEntries.forEach((entry) => {
    const currencyKey = entry.currency ?? "__none__";
    if (!byCurrency.has(currencyKey)) {
      byCurrency.set(currencyKey, {
        sum: 0,
        count: 0,
        currency: entry.currency ?? null,
      });
    }
    const bucket = byCurrency.get(currencyKey);
    bucket.sum += entry.targetCpa;
    bucket.count += 1;
  });

  const targetCpaParts = Array.from(byCurrency.values()).map((bucket) => ({
    targetCpa: bucket.sum / bucket.count,
    currency: bucket.currency,
  }));

  return {
    primaryKpi: primaryKpi.value,
    targetCpaParts,
  };
};

const getAdvertiserFullDataCacheRef = (advertiserID) =>
  ref(db, `ownerDashboardCaches/advertiserFullData/${advertiserID}`);

const isAdvertiserFullDataCacheFresh = (creationTime) => {
  if (creationTime == null || typeof creationTime !== "number") {
    return false;
  }
  return (
    differenceInHours(new Date(), new Date(creationTime)) <
    ADVERTISER_FULL_DATA_CACHE_HOURS
  );
};

const EMPTY_ADVERTISER_CAMPAIGNS = {
  activeCampaigns: 0,
  totalCampaigns: 0,
  hasRedCampaignAlerts: false,
  redCampaignAlerts: [],
  kpiSummary: {
    primaryKpi: null,
    targetCpaParts: [],
  },
};

const mapAdvertiserCampaignsResult = (campaignsResult) => {
  if (!campaignsResult?.ok || !campaignsResult.data) {
    return EMPTY_ADVERTISER_CAMPAIGNS;
  }

  return {
    activeCampaigns: campaignsResult.data.activeCampaigns,
    totalCampaigns: campaignsResult.data.totalCampaigns,
    hasRedCampaignAlerts: campaignsResult.data.hasRedCampaignAlerts,
    redCampaignAlerts: campaignsResult.data.redCampaignAlerts,
    kpiSummary: campaignsResult.data.kpiSummary,
  };
};

/**
 * Obtiene advertisers con paginación (similar a startGetAdsCampaigns)
 * @param {Object} options - Opciones de paginación
 * @param {string} options.lastKey - Última clave para continuar la paginación
 * @param {number} options.limit - Límite de registros por página (default: 12)
 * @param {Function} options.onChangeLastKey - Callback para actualizar lastKey
 * @param {Function} options.onLoadLastKey - Callback cuando no hay más datos
 * @returns {Object} - Lista paginada de advertisers
 */
export const startGetAdvertisersPaginated =
  (
    lastKey,
    onChangeLastKey = () => {},
    onLoadLastKey = () => {},
    limit = 12,
    restAdvertisers = {},
    filter = null,
    filterType = null,
    agenciesFetchedCache = {},
    emailIndexLastKey = null,
    onChangeEmailIndexLastKey = () => {},
    onLoadEmailIndexLastKey = () => {}
  ) =>
  async (dispatch) => {
    try {
      const dbRef = ref(db, `advertisers`);
      const isTextSearch = isClientTextSearch(filterType);

      // Cache of agencies already expanded (fetched via advertisersByAgency).
      // This is expected to be a mutable object passed from the UI layer via useRef.
      // Shape: { [agencyID: string]: true }
      const agencyCache = agenciesFetchedCache;
      let advertisersQuery;

      // Usar configuración de filtros si existe, sino usar lógica sin filtro
      if (
        filter !== null &&
        filterType !== null &&
        FILTER_CONFIGS[filterType]
      ) {
        const filterConfig = FILTER_CONFIGS[filterType];
        advertisersQuery = filterConfig.buildQuery(
          dbRef,
          filter,
          lastKey,
          limit
        );
      } else {
        // Sin filtro: usar lógica original con businessName
        if (lastKey) {
          // Continuar desde la última clave
          advertisersQuery = query(
            dbRef,
            orderByChild("businessName"),
            startAfter(lastKey),
            limitToFirst(limit)
          );
        } else {
          // Primera página
          advertisersQuery = query(
            dbRef,
            orderByChild("businessName"),
            limitToFirst(limit)
          );
        }
      }
      const shouldFetchPendingPayment =
        !lastKey && filterType === "isPlanActive" && filter === true;

      const pendingPaymentQuery = shouldFetchPendingPayment
        ? query(dbRef, orderByChild("isPendingPayment"), equalTo(true))
        : null;

      let emailIndexQuery = null;
      if (isTextSearch && filter) {
        const emailIndexRef = ref(db, "adsUsersIndex");
        emailIndexQuery = FILTER_CONFIGS.adsUsersIndexEmail.buildQuery(
          emailIndexRef,
          filter,
          emailIndexLastKey,
          limit
        );
      }

      const [mainSnapshot, pendingPaymentSnapshot, emailIndexSnapshot] =
        await Promise.all([
          get(advertisersQuery),
          pendingPaymentQuery
            ? get(pendingPaymentQuery)
            : Promise.resolve(null),
          emailIndexQuery ? get(emailIndexQuery) : Promise.resolve(null),
        ]);

      const {
        orderedKeys: emailIndexOrderedKeys,
        entries: emailIndexEntries,
        emailsByAgency,
      } = collectEmailIndexHitsFromSnapshot(emailIndexSnapshot);

      // forEach order matches orderByChild (Object.keys(val()) does not, per Firebase docs)
      const orderedQueryKeys = [];
      const advertisers = {};
      mainSnapshot.forEach((child) => {
        orderedQueryKeys.push(child.key);
        advertisers[child.key] = child.val();
      });

      const pendingOrderedKeys = [];
      const pendingAdvertisers = {};
      if (pendingPaymentSnapshot) {
        pendingPaymentSnapshot.forEach((child) => {
          pendingOrderedKeys.push(child.key);
          pendingAdvertisers[child.key] = child.val();
        });
      }

      const nameStreamExhausted =
        orderedQueryKeys.length === 0 && pendingOrderedKeys.length === 0;
      const emailStreamExhausted =
        !emailIndexQuery || emailIndexOrderedKeys.length === 0;

      if (nameStreamExhausted) {
        onLoadLastKey();
      }

      if (emailStreamExhausted && emailIndexQuery) {
        onLoadEmailIndexLastKey();
      }

      if (nameStreamExhausted && emailStreamExhausted) {
        return true;
      }

      const validDataPaginated = {};
      for (let i = 0; i < orderedQueryKeys.length; i++) {
        const advertiserKey = orderedQueryKeys[i];
        const advertiserData = advertisers[advertiserKey];
        await processAdvertiserRowIntoValidData({
          advertiserKey,
          advertiserData,
          validData: validDataPaginated,
          agencyCache,
          filter,
          filterType,
          dispatch,
        });
      }

      const validDataPending = {};
      if (shouldFetchPendingPayment && pendingOrderedKeys.length > 0) {
        for (let i = 0; i < pendingOrderedKeys.length; i++) {
          const advertiserKey = pendingOrderedKeys[i];
          const advertiserData = pendingAdvertisers[advertiserKey] || {};
          await processAdvertiserRowIntoValidData({
            advertiserKey,
            advertiserData,
            validData: validDataPending,
            agencyCache,
            filter,
            filterType,
            dispatch,
          });
        }
      }

      if (isTextSearch && !_.isEmpty(emailsByAgency)) {
        for (const agencyID of Object.keys(emailsByAgency)) {
          if (!isValidAgencyIDForSearch(agencyID)) {
            continue;
          }
          await expandAgencyIntoValidData({
            agencyID,
            excludeAdvertiserKey: null,
            validData: validDataPaginated,
            agencyCache,
            filter,
            filterType,
            dispatch,
          });
        }
      }

      const validData = {
        ...validDataPending,
        ...validDataPaginated,
      };

      if (isTextSearch) {
        applySearchMatchedEmailsToValidData({
          validData,
          emailsByAgency,
          searchTerm: filter,
        });
      }

      let paginationLastKey;
      if (orderedQueryKeys.length > 0) {
        if (
          filter !== null &&
          filterType !== null &&
          FILTER_CONFIGS[filterType]
        ) {
          const filterConfig = FILTER_CONFIGS[filterType];
          const extracted = filterConfig.extractLastKey(
            orderedQueryKeys,
            advertisers
          );
          if (
            filterType === "isPlanActive" &&
            orderedQueryKeys.length > 0 &&
            extracted != null
          ) {
            const lastAdvertiserID =
              orderedQueryKeys[orderedQueryKeys.length - 1];
            paginationLastKey = `${extracted}${IS_PLAN_ACTIVE_PAGE_CURSOR_SEP}${lastAdvertiserID}`;
          } else {
            paginationLastKey = extracted;
          }
        } else {
          const lastAdvertiserID =
            orderedQueryKeys[orderedQueryKeys.length - 1];
          const lastAdvertiserData = advertisers[lastAdvertiserID];
          paginationLastKey =
            lastAdvertiserData.businessName || lastAdvertiserID;
        }
      } else {
        paginationLastKey = null;
      }

      let emailPaginationLastKey = null;
      if (emailIndexOrderedKeys.length > 0) {
        emailPaginationLastKey =
          FILTER_CONFIGS.adsUsersIndexEmail.extractLastKey(
            emailIndexOrderedKeys,
            emailIndexEntries
          );
      }

      if (paginationLastKey != null && paginationLastKey === lastKey) {
        onLoadLastKey();
      }

      if (
        emailPaginationLastKey != null &&
        emailPaginationLastKey === emailIndexLastKey
      ) {
        onLoadEmailIndexLastKey();
      }

      if (_.isEmpty(validData)) {
        const canRetryName =
          paginationLastKey != null && paginationLastKey !== lastKey;
        const canRetryEmail =
          emailPaginationLastKey != null &&
          emailPaginationLastKey !== emailIndexLastKey;

        if (!canRetryName && !canRetryEmail) {
          return true;
        }

        await waitDelay(200);
        return dispatch(
          startGetAdvertisersPaginated(
            canRetryName ? paginationLastKey : lastKey,
            onChangeLastKey,
            onLoadLastKey,
            parseInt(limit * 1.5),
            restAdvertisers,
            filter,
            filterType,
            agencyCache,
            canRetryEmail ? emailPaginationLastKey : emailIndexLastKey,
            onChangeEmailIndexLastKey,
            onLoadEmailIndexLastKey
          )
        );
      }

      if (paginationLastKey) {
        onChangeLastKey(paginationLastKey);
      }

      if (emailPaginationLastKey) {
        onChangeEmailIndexLastKey(emailPaginationLastKey);
      }

      // Full replace key order = merge order (Redux spread merge would keep stale key order).
      const mergedAdvertisers = lastKey
        ? { ...restAdvertisers, ...validData }
        : { ...validData, ...restAdvertisers };
      dispatch(getAdvertisers(mergedAdvertisers));

      return mergedAdvertisers;
    } catch (error) {
      console.error("Error en startGetAdvertisersPaginated:", error);
      dispatch(LaunchError(error, false));
      return {
        ok: false,
        message: "Error al obtener advertisers paginados",
        error: error.message,
      };
    }
  };

export const startGetAdvertisersByAgency =
  (agencyID, filterAdvertiserKey, filter = null, filterType = null) =>
  async (dispatch) => {
    void filter;
    const isTextSearch = isClientTextSearch(filterType);
    try {
      const dbRef = ref(db, `advertisersByAgency/${agencyID}`);
      const snapshot = await get(dbRef);

      const raw = snapshot.val();
      const advertisersKeys = Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object"
          ? Object.keys(raw)
          : [];

      const restAdvertisersKeys = advertisersKeys.filter(
        (key) => key !== filterAdvertiserKey
      );

      if (restAdvertisersKeys.length === 0) {
        return { ok: true, data: {} };
      }

      // Fetch all advertiser records in parallel (agencies typically have small lists).
      const snapshots = await Promise.all(
        restAdvertisersKeys.map((id) => get(ref(db, `advertisers/${id}`)))
      );

      const restAdvertisers = {};

      for (let i = 0; i < restAdvertisersKeys.length; i++) {
        const id = restAdvertisersKeys[i];
        const advertiserData = snapshots[i].val() || {};

        if (isAdvertiserExcludedFromClientsList(advertiserData)) {
          continue;
        }

        if (!isTextSearch) {
          const hasPlan =
            advertiserData.subscriptionPlan &&
            advertiserData.subscriptionPlan !== WITH_OUT_PLAN &&
            advertiserData.subscriptionPlanInfo !== null;
          // Siblings are included for full agency grouping; status filter applies
          // only to the seed advertiser from the paginated/pending query.
          if (
            advertiserData?.isAppSumo === true ||
            hasHiddenDefaultClientsListPlanPrice(advertiserData) ||
            !hasPlan
          ) {
            continue;
          }
        }

        const creativesUsed =
          (advertiserData?.subscriptionPlanInfo?.benefits?.creatives ?? 0) -
          (advertiserData?.subscriptionPlanInfo?.usage?.creatives ?? 0);
        const cancellationData =
          advertiserData?.subscriptionPlanInfo?.cancelReason || null;

        restAdvertisers[id] = {
          id,
          businessName: advertiserData.businessName || "-",
          creationTime: advertiserData.creationTime ?? null,
          agencyID: advertiserData.agencyID,
          isAppSumo: advertiserData?.isAppSumo ?? false,
          hasAddonSpendFee:
            advertiserData?.hasAddonSpendFee ??
            advertiserData?.hasAddon ??
            false,
          recurrent: advertiserData?.subscriptionPlanInfo?.recurrent || null,
          typeOfPlans: advertiserData.typeOfPlans || null,
          subscriptionPlan: advertiserData.subscriptionPlan || WITH_OUT_PLAN,
          subscriptionPlanInfo: advertiserData.subscriptionPlanInfo || null,
          isPlanActive: advertiserData.isPlanActive,
          isPendingPayment: Boolean(advertiserData?.isPendingPayment),
          healthScore: advertiserData.healthScore ?? null,
          healthScoreIsPlanActive:
            advertiserData.healthScoreIsPlanActive ?? null,
          kpiSummary: advertiserData.kpiSummary ?? null,
          creativesUsed,
          searchMatchedEmail: null,
          cancellationData: cancellationData
            ? {
                reason: cancellationData.reason,
                selectedReason: cancellationData.selectedReason,
                cancelDateTime: cancellationData.cancelDateTime,
              }
            : null,
        };
      }
      return {
        ok: true,
        data: restAdvertisers,
      };
    } catch (error) {
      console.error("Error en startGetAdvertisersByAgency:", error);
      dispatch(LaunchError(error, false));
      return {
        ok: false,
        message: "Error al obtener advertisers por agency",
        error: error.message,
      };
    }
  };

const sortAgencyExtraKeysForAdvertisersPaginated = (extra) => {
  return Object.keys(extra).sort((a, b) => {
    const va = extra[a]?.healthScoreIsPlanActive;
    const vb = extra[b]?.healthScoreIsPlanActive;
    if (va == null && vb == null) return a.localeCompare(b);
    if (va == null) return 1;
    if (vb == null) return -1;
    if (va !== vb) return va < vb ? -1 : 1;
    return a.localeCompare(b);
  });
};

const expandAgencyIntoValidData = async ({
  agencyID,
  excludeAdvertiserKey = null,
  validData,
  agencyCache,
  filter,
  filterType,
  dispatch,
}) => {
  if (!agencyID || agencyCache[agencyID]) {
    return;
  }

  agencyCache[agencyID] = true;

  const responseRestAdvertisers = await dispatch(
    startGetAdvertisersByAgency(
      agencyID,
      excludeAdvertiserKey,
      filter,
      filterType
    )
  );

  if (!responseRestAdvertisers.ok) {
    return;
  }

  const extra = responseRestAdvertisers.data || {};
  for (const id of sortAgencyExtraKeysForAdvertisersPaginated(extra)) {
    if (!validData[id]) {
      validData[id] = extra[id];
    }
  }
};

/**
 * Mutates validData and agencyCache. Expands agency siblings via startGetAdvertisersByAgency.
 */
const processAdvertiserRowIntoValidData = async ({
  advertiserKey,
  advertiserData,
  validData,
  agencyCache,
  filter,
  filterType,
  dispatch,
}) => {
  const isTextSearch = isClientTextSearch(filterType);
  const hasPlan =
    advertiserData.subscriptionPlan &&
    advertiserData.subscriptionPlan !== WITH_OUT_PLAN &&
    advertiserData.subscriptionPlanInfo !== null;
  if (isAdvertiserExcludedFromClientsList(advertiserData)) {
    return;
  }

  if (!isTextSearch && !hasPlan) {
    return;
  }

  if (!isTextSearch && advertiserData?.isAppSumo === true) {
    return;
  }

  if (!isTextSearch && hasHiddenDefaultClientsListPlanPrice(advertiserData)) {
    return;
  }

  if (
    !isTextSearch &&
    !matchesIsPlanActiveFilter(advertiserData, filter, filterType)
  ) {
    return;
  }

  const agencyID = advertiserData.agencyID;

  if (agencyID) {
    await expandAgencyIntoValidData({
      agencyID,
      excludeAdvertiserKey: advertiserKey,
      validData,
      agencyCache,
      filter,
      filterType,
      dispatch,
    });
  }

  const creativesUsed =
    (advertiserData?.subscriptionPlanInfo?.benefits?.creatives ?? 0) -
    (advertiserData?.subscriptionPlanInfo?.usage?.creatives ?? 0);
  const cancellationData =
    advertiserData?.subscriptionPlanInfo?.cancelReason || null;

  validData[advertiserKey] = {
    id: advertiserKey,
    businessName: advertiserData.businessName || "-",
    creationTime: advertiserData.creationTime ?? null,
    agencyID: advertiserData.agencyID,
    isAppSumo: advertiserData?.isAppSumo ?? false,
    hasAddonSpendFee:
      advertiserData?.hasAddonSpendFee ?? advertiserData?.hasAddon ?? false,
    recurrent: advertiserData?.subscriptionPlanInfo?.recurrent || null,
    typeOfPlans: advertiserData.typeOfPlans || null,
    subscriptionPlan: advertiserData.subscriptionPlan || WITH_OUT_PLAN,
    subscriptionPlanInfo: advertiserData.subscriptionPlanInfo || null,
    isPlanActive: advertiserData.isPlanActive,
    healthScore: advertiserData.healthScore ?? null,
    healthScoreIsPlanActive: advertiserData.healthScoreIsPlanActive ?? null,
    kpiSummary: advertiserData.kpiSummary ?? null,
    creativesUsed,
    isPendingPayment: Boolean(advertiserData?.isPendingPayment),
    searchMatchedEmail: null,
    cancellationData: cancellationData
      ? {
          reason: cancellationData.reason,
          selectedReason: cancellationData.selectedReason,
          cancelDateTime: cancellationData.cancelDateTime,
        }
      : null,
  };
};

export const startGetUsersByAdvertiser = (advertiserID) => async (dispatch) => {
  try {
    const dbRef = ref(db, `adsUsers/${advertiserID}`);
    const snapshot = await get(dbRef);

    if (!snapshot.exists()) {
      return {
        ok: true,
        data: {
          users: [],
          totalLoginsLast30Days: 0,
          daysSinceLastLogin: null,
        },
      };
    }

    const users = [];
    let totalLoginsLast30Days = 0;
    let latestLoginTimestamp = null;
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

    snapshot.forEach((child) => {
      const userData = child.val();
      const lastLogins = userData.lastLogins || [];

      // Filtrar logins de los últimos 30 días
      const recentLogins = lastLogins.filter(
        (timestamp) => timestamp > thirtyDaysAgo
      );
      totalLoginsLast30Days += recentLogins.length;

      // Encontrar el login más reciente
      if (lastLogins.length > 0) {
        const userLatestLogin = Math.max(...lastLogins);
        if (!latestLoginTimestamp || userLatestLogin > latestLoginTimestamp) {
          latestLoginTimestamp = userLatestLogin;
        }
      }

      users.push({
        uid: child.key,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        lastLogins: lastLogins,
        recentLogins: recentLogins,
        totalRecentLogins: recentLogins.length,
      });
    });

    // Calcular días desde el último login
    const daysSinceLastLogin = latestLoginTimestamp
      ? Math.floor((Date.now() - latestLoginTimestamp) / (1000 * 60 * 60 * 24))
      : null;

    const result = {
      ok: true,
      data: {
        users,
        totalLoginsLast30Days,
        daysSinceLastLogin,
        latestLoginTimestamp,
      },
    };

    return result;
  } catch (error) {
    console.error("Error en startGetUsersByAdvertiser:", error);
    dispatch(LaunchError(error, false));
    return {
      ok: false,
      message: "Error al obtener usuarios del advertiser",
      error: error.message,
    };
  }
};

export const startGetAdvertiserActiveCampaigns =
  (advertiserID) => async (dispatch) => {
    try {
      const campaignPath = `adsCampaigns/${advertiserID}`;
      const adGroupsPath = `adsGroups/${advertiserID}`;
      const dbRef = ref(db, campaignPath);

      const campaignsQuery = query(
        dbRef,
        orderByChild("states/general"),
        equalTo(ADS_CAMPAIGN_STATUS_ACTIVE)
      );

      const snapshot = await get(campaignsQuery);

      if (!snapshot.exists()) {
        return {
          ok: true,
          data: {
            advertiserID,
            activeCampaigns: 0,
            totalCampaigns: 0,
            hasRedCampaignAlerts: false,
            redCampaignAlerts: [],
            kpiSummary: {
              primaryKpi: null,
              targetCpaParts: [],
            },
          },
        };
      }

      let activeCampaigns = 0;
      const activeCampaignsData = {};
      const adGroupIDs = new Set();

      snapshot.forEach((child) => {
        activeCampaigns++;
        const campaignData = child.val() || {};
        activeCampaignsData[child.key] = campaignData;

        Object.keys(campaignData?.adsGroups || {}).forEach((adGroupID) => {
          adGroupIDs.add(adGroupID);
        });
      });

      const adsGroupsByID = {};
      const adGroupSnapshots = await Promise.allSettled(
        Array.from(adGroupIDs).map((adGroupID) =>
          get(ref(db, `${adGroupsPath}/${adGroupID}`))
        )
      );

      adGroupSnapshots.forEach((result) => {
        if (result.status !== "fulfilled") return;

        const adGroupData = result.value.val();
        const adGroupID = adGroupData?.id;

        if (!adGroupData || !adGroupID) return;

        adsGroupsByID[adGroupID] = {
          id: adGroupID,
          platform: adGroupData.platform,
          bidGoal: adGroupData.bidGoal,
        };
      });

      const itemStatuses = await fetchAlertItemStatuses({
        campaigns: activeCampaignsData,
        paths: {
          pathAds: `ads/${advertiserID}`,
          pathAdSets: `adsGroups/${advertiserID}`,
          pathAssetsGroups: `assetsGroups/${advertiserID}`,
        },
      });
      const { hasRedCampaignAlerts, redCampaignAlerts } =
        getRedAlertsSummaryFromCampaigns({
          campaigns: activeCampaignsData,
          itemStatuses,
        });
      const kpiSummary = getAdvertiserKpiSummary({
        campaigns: activeCampaignsData,
        adsGroupsByID,
      });

      const result = {
        ok: true,
        data: {
          advertiserID,
          activeCampaigns,
          totalCampaigns: activeCampaigns,
          hasRedCampaignAlerts,
          redCampaignAlerts,
          kpiSummary,
        },
      };

      return result;
    } catch (error) {
      console.error("Error en startGetAdvertiserActiveCampaigns:", error);
      dispatch(LaunchError(error, false));
      return {
        ok: false,
        message: "Error al obtener campañas activas del advertiser",
        error: error.message,
      };
    }
  };

export const startGetAdvertiserOptimizations =
  (advertiserID) => async (dispatch) => {
    try {
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

      // Campaign IDs are needed to scope the logs query per campaign — logs are
      // nested under adsCampaignsLogs/{advertiserID}/{campaignID}/{logID}, so
      // creationTime can only be filtered server-side one level below this.
      const campaignsSnapshot = await get(
        ref(db, `adsCampaigns/${advertiserID}`)
      );
      const campaignIDs = campaignsSnapshot.exists()
        ? Object.keys(campaignsSnapshot.val() || {})
        : [];

      if (campaignIDs.length === 0) {
        return {
          ok: true,
          data: {
            advertiserID,
            totalOptimizations: 0,
            appliedOptimizations: 0,
          },
        };
      }

      let totalOptimizations = 0;
      let appliedOptimizations = 0;

      const campaignLogsSnapshots = await Promise.allSettled(
        campaignIDs.map((campaignID) =>
          get(
            query(
              ref(db, `adsCampaignsLogs/${advertiserID}/${campaignID}`),
              orderByChild("creationTime"),
              startAt(thirtyDaysAgo)
            )
          )
        )
      );

      campaignLogsSnapshots.forEach((result) => {
        if (result.status !== "fulfilled" || !result.value.exists()) return;

        result.value.forEach((logSnapshot) => {
          const logData = logSnapshot.val();
          if (logData?.changes && Array.isArray(logData.changes)) {
            logData.changes.forEach((change) => {
              if (change.objectType === "insight") {
                totalOptimizations++;

                if (change.newValue && change.newValue.isApplied === true) {
                  appliedOptimizations++;
                }
              }
            });
          }
        });
      });

      const result = {
        ok: true,
        data: {
          advertiserID,
          totalOptimizations,
          appliedOptimizations,
        },
      };

      return result;
    } catch (error) {
      console.error("Error en startGetAdvertiserOptimizations:", error);
      dispatch(LaunchError(error, false));
      return {
        ok: false,
        message: "Error al obtener optimizaciones del advertiser",
        error: error.message,
      };
    }
  };

export const startGetAdvertiserAICreativesLast30Days =
  (advertiserID) => async (dispatch) => {
    try {
      const contentPath = `contentList/${advertiserID}`;
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      // Filtered server-side by creationTime — RTDB does not support compound
      // queries, so isAIGenerated is still filtered client-side below.
      const contentQuery = query(
        ref(db, contentPath),
        orderByChild("creationTime"),
        startAt(thirtyDaysAgo)
      );
      const snapshot = await get(contentQuery);

      if (!snapshot.exists()) {
        return {
          ok: true,
          data: {
            advertiserID,
            aiCreativesLast30Days: 0,
          },
        };
      }

      let aiCreativesLast30Days = 0;

      snapshot.forEach((contentSnapshot) => {
        const contentData = contentSnapshot.val();
        if (contentData.isAIGenerated === true) {
          aiCreativesLast30Days++;
        }
      });
      const result = {
        ok: true,
        data: {
          advertiserID,
          aiCreativesLast30Days,
        },
      };

      return result;
    } catch (error) {
      console.error("Error en startGetAdvertiserAICreativesLast30Days:", error);
      dispatch(LaunchError(error, false));
      return {
        ok: false,
        message: "Error al obtener creativos con IA del advertiser",
        error: error.message,
      };
    }
  };

export const startGetAgencyActiveUsers = (agencyID) => async (dispatch) => {
  try {
    const usersPath = `adsUsers/${agencyID}`;
    const dbRef = ref(db, usersPath);
    const snapshot = await get(dbRef);

    if (!snapshot.exists()) {
      return {
        ok: true,
        data: {
          agencyID,
          activeUsers: 0,
        },
      };
    }

    let activeUsers = 0;

    snapshot.forEach((child) => {
      const userData = child.val();

      if (userData.isDeleted !== true) {
        activeUsers++;
      }
    });

    const result = {
      ok: true,
      data: {
        agencyID,
        activeUsers,
      },
    };

    return result;
  } catch (error) {
    console.error("Error en startGetAgencyActiveUsers:", error);
    dispatch(LaunchError(error, false));
    return {
      ok: false,
      message: "Error al obtener usuarios activos de la agencia",
      error: error.message,
    };
  }
};

export const startGetAllAgenciesActiveUsers =
  (agencyIDs) => async (dispatch) => {
    try {
      const results = {};

      const promises = agencyIDs.map(async (agencyID) => {
        const result = await dispatch(startGetAgencyActiveUsers(agencyID));
        if (result.ok) {
          results[agencyID] = result.data.activeUsers;
        } else {
          results[agencyID] = 0;
          console.error(
            `Error obteniendo usuarios para ${agencyID}:`,
            result.message
          );
        }
      });

      await Promise.all(promises);

      return {
        ok: true,
        data: {
          activeUsersByAgency: results,
        },
      };
    } catch (error) {
      console.error("Error en startGetAllAgenciesActiveUsers:", error);
      dispatch(LaunchError(error, false));
      return {
        ok: false,
        message: "Error al obtener usuarios activos de todas las agencias",
        error: error.message,
      };
    }
  };

export const startGetAgencyName = (agencyID) => async (dispatch) => {
  try {
    const dbRef = ref(db, `agencies/${agencyID}`);
    const snapshot = await get(dbRef);

    if (!snapshot.exists()) {
      return {
        ok: false,
        message: "Agency not found",
      };
    }

    const agencyData = snapshot.val() || {};

    return {
      ok: true,
      data: {
        agencyID,
        name: agencyData.name,
        isAppSumo: agencyData.isAppSumo ?? false,
        discoverySource: agencyData.discoverySource ?? null,
        imageProvider: resolveAgencyImageProvider(agencyData.imageProvider),
        hasMinimumTermContractClause:
          agencyData.hasMinimumTermContractClause === true,
      },
    };
  } catch (error) {
    console.error("Error en startGetAgencyName:", error);
    dispatch(LaunchError(error, false));
    return {
      ok: false,
      message: "Error al obtener nombre de la agencia",
      error: error.message,
    };
  }
};

export const startSaveAgencyName =
  ({ agencyID, name }) =>
  async (dispatch) => {
    try {
      await update(ref(db), {
        [`agencies/${agencyID}/name`]: name,
      });
      return { ok: true };
    } catch (error) {
      console.error("Error en startSaveAgencyName:", error);
      dispatch(LaunchError(error, false));
      return { ok: false };
    }
  };

export const startGetAgenciesNames = (agencyIDs) => async (dispatch) => {
  try {
    const results = {};

    const promises = agencyIDs.map(async (agencyID) => {
      const result = await dispatch(startGetAgencyName(agencyID));
      if (result.ok) {
        results[agencyID] = {
          name: result.data.name,
          isAppSumo: result.data.isAppSumo,
          discoverySource: result.data.discoverySource ?? null,
          imageProvider:
            result.data.imageProvider ?? DEFAULT_AGENCY_IMAGE_PROVIDER,
          hasMinimumTermContractClause:
            result.data.hasMinimumTermContractClause === true,
        };
      } else {
        results[agencyID] = null;
      }
    });

    await Promise.all(promises);

    return {
      ok: true,
      data: {
        agenciesByAgency: results,
      },
    };
  } catch (error) {
    console.error("Error en startGetAgenciesNames:", error);
    dispatch(LaunchError(error, false));
    return {
      ok: false,
      message: "Error al obtener nombres de todas las agencias",
      error: error.message,
    };
  }
};

export const startGetAllAdvertisersOptimizations =
  (advertiserIDs) => async (dispatch) => {
    try {
      const totalResults = {};
      const appliedResults = {};

      const promises = advertiserIDs.map(async (advertiserID) => {
        const result = await dispatch(
          startGetAdvertiserOptimizations(advertiserID)
        );
        if (result.ok) {
          totalResults[advertiserID] = result.data.totalOptimizations;
          appliedResults[advertiserID] = result.data.appliedOptimizations;
        } else {
          totalResults[advertiserID] = 0;
          appliedResults[advertiserID] = 0;
          console.error(
            `Error obteniendo optimizaciones para ${advertiserID}:`,
            result.message
          );
        }
      });

      await Promise.all(promises);

      return {
        ok: true,
        data: {
          totalOptimizationsByAdvertiser: totalResults,
          appliedOptimizationsByAdvertiser: appliedResults,
        },
      };
    } catch (error) {
      console.error("Error en startGetAllAdvertisersOptimizations:", error);
      dispatch(LaunchError(error, false));
      return {
        ok: false,
        message: "Error al obtener optimizaciones de todos los advertisers",
        error: error.message,
      };
    }
  };

const mergeAgencyCacheEntry = (cache, agencyID, patch) => {
  if (!agencyID) return;
  cache[agencyID] = { ...cache[agencyID], ...patch };
};

const getAgencyUsersFromCache = (cache, agencyID) => {
  const entry = cache[agencyID];
  if (!entry?.usersLoaded) return null;
  return {
    users: entry.users ?? [],
    totalLoginsLast30Days: entry.totalLoginsLast30Days ?? 0,
    daysSinceLastLogin: entry.daysSinceLastLogin ?? null,
    activeUsers: entry.activeUsers ?? 0,
  };
};

const setAgencyUsersInCache = (cache, agencyID, usersData) => {
  mergeAgencyCacheEntry(cache, agencyID, {
    ...usersData,
    usersLoaded: true,
  });
};

const isAdsUserDeleted = (userData) => Boolean(userData?.isDeleted);

const EMPTY_AGENCY_USERS_DATA = {
  users: [],
  totalLoginsLast30Days: 0,
  daysSinceLastLogin: null,
  activeUsers: 0,
};

const fetchAgencyAdsUsersData = async ({ agencyID, thirtyDaysAgo }) => {
  if (!agencyID) {
    return { ...EMPTY_AGENCY_USERS_DATA };
  }

  const dbRef = ref(db, `adsUsers/${agencyID}`);
  const snapshot = await get(dbRef);

  if (!snapshot.exists()) {
    return { ...EMPTY_AGENCY_USERS_DATA };
  }

  const users = [];
  let totalLoginsLast30Days = 0;
  let latestLoginTimestamp = null;
  let activeUsers = 0;

  snapshot.forEach((child) => {
    const userData = child.val();
    const lastLogins = userData.lastLogins || [];
    const deleted = isAdsUserDeleted(userData);

    if (!deleted) {
      activeUsers++;
    }

    const recentLogins = lastLogins.filter(
      (timestamp) => timestamp > thirtyDaysAgo
    );
    totalLoginsLast30Days += recentLogins.length;

    if (lastLogins.length > 0) {
      const userLatestLogin = Math.max(...lastLogins);
      if (!latestLoginTimestamp || userLatestLogin > latestLoginTimestamp) {
        latestLoginTimestamp = userLatestLogin;
      }
    }

    users.push({
      uid: child.key,
      email: userData.email,
      name: userData.name,
      role: userData.role,
      isDeleted: deleted,
      lastLogins,
      recentLogins,
      totalRecentLogins: recentLogins.length,
    });
  });

  const daysSinceLastLogin = latestLoginTimestamp
    ? Math.floor((Date.now() - latestLoginTimestamp) / (1000 * 60 * 60 * 24))
    : null;

  return {
    users,
    totalLoginsLast30Days,
    daysSinceLastLogin,
    activeUsers,
  };
};

/**
 * Obtiene todos los datos de UN advertiser individual
 * @param {string} advertiserID - ID del advertiser
 * @param {string} agencyID - ID de la agencia del advertiser
 * @param {Object} agencyCache - Cache unificado por agencyID (usuarios + perfil RTDB)
 * @returns {Object} - Datos completos del advertiser
 */
export const startGetAdvertiserFullData =
  (advertiserID, agencyID, agencyCache = {}) =>
  async (dispatch) => {
    try {
      const metricsRange = getDateRangeFromPreset(DATE_RANGE.last_7);
      const metricsWindow = {
        startDateTime: metricsRange.startDateTime.toISOString(),
        endDateTime: metricsRange.endDateTime.toISOString(),
      };
      const cacheSnapshot = await get(
        getAdvertiserFullDataCacheRef(advertiserID)
      );
      if (cacheSnapshot.exists()) {
        const cache = cacheSnapshot.val();
        const cachedData = cache?.data;
        const cachedAdvertiserID = cache?.advertiserID;
        const cachedAgencyID = cache?.agencyID;

        const agencyMatches = cachedAgencyID === agencyID;
        const advertiserMatches = cachedAdvertiserID === advertiserID;
        const metricsWindowMatches =
          cache?.metricsWindow?.startDateTime === metricsWindow.startDateTime &&
          cache?.metricsWindow?.endDateTime === metricsWindow.endDateTime;

        if (
          cachedData &&
          isAdvertiserFullDataCacheFresh(cache.creationTime) &&
          advertiserMatches &&
          agencyMatches &&
          metricsWindowMatches
        ) {
          const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
          // loginMetrics, campaigns, optimizations and aiCreativesLast30Days
          // are always fetched live. metrics and npsRating stay on the 24h cache.
          const [
            usersData,
            campaignsResult,
            optimizationsResult,
            aiCreativesResult,
          ] = await Promise.all([
            fetchAgencyAdsUsersData({ agencyID, thirtyDaysAgo }),
            dispatch(startGetAdvertiserActiveCampaigns(advertiserID)),
            dispatch(startGetAdvertiserOptimizations(advertiserID)),
            dispatch(startGetAdvertiserAICreativesLast30Days(advertiserID)),
          ]);

          if (agencyID) {
            setAgencyUsersInCache(agencyCache, agencyID, usersData);
          }

          return {
            ok: true,
            data: {
              ...cachedData,
              loginMetrics: {
                ...cachedData.loginMetrics,
                users: usersData.users,
                totalLoginsLast30Days: usersData.totalLoginsLast30Days,
                daysSinceLastLogin: usersData.daysSinceLastLogin,
              },
              activeUsers: usersData.activeUsers,
              agencyData: usersData,
              usersFromCache: true,
              campaigns: mapAdvertiserCampaignsResult(campaignsResult),
              optimizations: optimizationsResult.ok
                ? {
                    appliedOptimizations:
                      optimizationsResult.data.appliedOptimizations,
                    totalOptimizations:
                      optimizationsResult.data.totalOptimizations,
                  }
                : { appliedOptimizations: 0, totalOptimizations: 0 },
              aiCreativesLast30Days: aiCreativesResult.ok
                ? aiCreativesResult.data.aiCreativesLast30Days
                : 0,
            },
          };
        }
      }
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

      // Execute advertiser-specific queries in parallel (campaigns, optimizations, metrics, aiCreatives)
      const [
        campaignsResult,
        optimizationsResult,
        metricsResult,
        aiCreativesResult,
      ] = await Promise.all([
        dispatch(startGetAdvertiserActiveCampaigns(advertiserID)),
        dispatch(startGetAdvertiserOptimizations(advertiserID)),
        dispatch(
          startGetTotalStatisticsAllCampaigns({
            advertiserID,
            startDateTime: metricsRange.startDateTime,
            endDateTime: metricsRange.endDateTime,
          })
        ),
        dispatch(startGetAdvertiserAICreativesLast30Days(advertiserID)),
      ]);

      // Get NPS rating (latest user creation per agency)
      let npsRating = 0;
      try {
        const npsRef = ref(db, `nps/${agencyID}`);
        const npsSnapshot = await get(npsRef);
        const npsData = npsSnapshot.val();
        if (!_.isEmpty(npsData)) {
          // New structure: map of userID -> { calificacion, creationTime, ... }
          const userEntries = Object.values(npsData).filter(
            (userNpsData) =>
              typeof userNpsData?.calificacion === "number" &&
              typeof userNpsData?.creationTime === "number"
          );

          if (userEntries.length === 0) {
            // Keep loading flow alive even when legacy/invalid NPS entries exist.
          }

          // Get the score from the most recently created user
          const latestUserEntry = userEntries.reduce((latest, current) => {
            if (!latest) return current;
            return current.creationTime > latest.creationTime
              ? current
              : latest;
          }, null);

          if (latestUserEntry) {
            npsRating = latestUserEntry.calificacion || 0;
          }
        }
      } catch (npsError) {
        dispatch(
          LaunchError(npsError, false, [], {
            functionName: "startGetAdvertiserFullData",
            capture: "exception",
          })
        );
        console.error(`Error getting NPS for agency ${agencyID}:`, npsError);
      }

      // Get users data by agencyID — always fresh from Firebase on first load per agency
      let usersData = { ...EMPTY_AGENCY_USERS_DATA };
      let usersFromCache = false;

      if (agencyID) {
        const cachedUsersData = getAgencyUsersFromCache(agencyCache, agencyID);
        if (cachedUsersData) {
          usersData = cachedUsersData;
          usersFromCache = true;
        } else {
          usersData = await fetchAgencyAdsUsersData({
            agencyID,
            thirtyDaysAgo,
          });
          setAgencyUsersInCache(agencyCache, agencyID, usersData);
        }
      }

      // Build response object
      const result = {
        ok: true,
        data: {
          advertiserID,
          agencyID,
          usersFromCache,
          loginMetrics: {
            totalLoginsLast30Days: usersData.totalLoginsLast30Days,
            daysSinceLastLogin: usersData.daysSinceLastLogin,
            users: usersData.users,
          },
          activeUsers: usersData.activeUsers,
          campaigns: mapAdvertiserCampaignsResult(campaignsResult),
          optimizations: optimizationsResult.ok
            ? {
                appliedOptimizations:
                  optimizationsResult.data.appliedOptimizations,
                totalOptimizations: optimizationsResult.data.totalOptimizations,
              }
            : { appliedOptimizations: 0, totalOptimizations: 0 },
          aiCreativesLast30Days: aiCreativesResult.ok
            ? aiCreativesResult.data.aiCreativesLast30Days
            : 0, // Nueva propiedad
          metrics:
            metricsResult.ok && metricsResult.data?.totals
              ? {
                  totals: metricsResult.data.totals,
                  totalsLastPeriod: metricsResult.data.totalsLastPeriod,
                }
              : { totals: {}, totalsLastPeriod: {} },
          npsRating,
          // Data to cache by agencyID
          agencyData: usersData,
        },
      };

      try {
        // Campaigns, optimizations and aiCreativesLast30Days are always
        // recomputed live, so persisting them would serve stale alerts.
        const cacheableData = _.omit(result.data, [
          "campaigns",
          "optimizations",
          "aiCreativesLast30Days",
        ]);

        await set(
          getAdvertiserFullDataCacheRef(advertiserID),
          replaceUndefinedWithNull({
            advertiserID,
            agencyID,
            creationTime: Date.now(),
            metricsWindow,
            data: cacheableData,
          })
        );
      } catch (persistError) {
        dispatch(
          LaunchError(persistError, false, [], {
            functionName: "startGetAdvertiserFullData",
            capture: "exception",
          })
        );
        console.error(
          `Failed to persist owner advertiser full data cache for ${advertiserID}:`,
          persistError
        );
      }

      return result;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startGetAdvertiserFullData",
          capture: "exception",
        })
      );
      console.error(
        `Error in startGetAdvertiserFullData for ${advertiserID}:`,
        error
      );
      return {
        ok: false,
        message: "Error getting advertiser full data",
        error: error.message,
      };
    }
  };

/**
 * Writes admin-calculated health score and composite key for orderByChild:
 * - advertisers/{id}/healthScore (number, no padding)
 * - advertisers/{id}/healthScoreIsPlanActive → "{true|false|null}-{padded0-100}"
 */
export const startPersistAdvertiserHealthScore =
  ({ advertiserID, healthScore, isPlanActive }) =>
  async (dispatch, getState) => {
    try {
      if (!advertiserID?.trim()) {
        return { ok: false, message: "Missing advertiserID" };
      }
      if (typeof healthScore !== "number" || !Number.isFinite(healthScore)) {
        return { ok: false, message: "Invalid healthScore" };
      }

      const adv = getState()?.owner?.advertisers?.[advertiserID];

      const healthScoreIsPlanActive = formatHealthScoreIsPlanActive({
        healthScore,
        isPlanActive,
      });

      await update(ref(db), {
        [`advertisers/${advertiserID}/healthScore`]: healthScore,
        [`advertisers/${advertiserID}/healthScoreIsPlanActive`]:
          healthScoreIsPlanActive,
      });

      if (adv) {
        dispatch(
          getNextAdvertisers({
            [advertiserID]: {
              ...adv,
              healthScore,
              healthScoreIsPlanActive,
            },
          })
        );
      }

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startPersistAdvertiserHealthScore",
          capture: "exception",
        })
      );
      console.error(
        `startPersistAdvertiserHealthScore ${advertiserID}:`,
        error
      );
      return { ok: false, message: error.message };
    }
  };

// Redux action creators
export const getOwnerBasicData = (data) => ({
  type: types.GET_OWNER_BASIC_DATA,
  payload: data,
});

export const getAdvertiserActiveCampaigns = (data) => ({
  type: types.GET_ADVERTISER_ACTIVE_CAMPAIGNS,
  payload: data,
});

export const getAdvertisers = (data) => ({
  type: types.GET_ADVERTISERS,
  payload: data,
});

export const getNextAdvertisers = (data) => ({
  type: types.GET_NEXT_ADVERTISERS,
  payload: data,
});

export const getAdvertiserOptimizations = (data) => ({
  type: types.GET_ADVERTISER_OPTIMIZATIONS,
  payload: data,
});

export const getAgencyActiveUsers = (data) => ({
  type: types.GET_AGENCY_ACTIVE_USERS,
  payload: data,
});

export const getAdvertiserSubscriptionPlan = (data) => ({
  type: types.GET_ADVERTISER_SUBSCRIPTION_PLAN,
  payload: data,
});

/**
 * Persists admin-defined KPI / target CPA as advertisers/{id}/kpiSummary.
 * Passing null clears it so campaign-derived KPI is shown again.
 */
export const startUpdateAdvertiserKpiCpaManual =
  ({ advertiserID, kpiSummary }) =>
  async (dispatch, getState) => {
    try {
      if (!advertiserID) {
        return {
          ok: false,
          message: "Missing advertiserID",
          code: "MISSING_PARAMETERS",
        };
      }

      const user = auth?.currentUser;
      if (!user) {
        return {
          ok: false,
          message: "User not authenticated",
          code: "UNAUTHORIZED",
        };
      }

      await update(
        ref(db, `advertisers/${advertiserID}/kpiSummary`),
        kpiSummary
      );

      const current = getState()?.owner?.advertisers?.[advertiserID] || {};
      dispatch(
        getNextAdvertisers({
          [advertiserID]: {
            ...current,
            kpiSummary,
          },
        })
      );

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUpdateAdvertiserKpiCpaManual",
          context: { advertiserID },
          tags: { operation: "update_kpi_cpa_manual" },
        })
      );
      return {
        ok: false,
        message: error?.message,
      };
    }
  };

/**
 * Crea un nuevo advertiser (marca) bajo una agencia existente
 * @param {string} agencyID - ID de la agencia
 * @param {string} businessName - Nombre de la marca
 * @returns {Promise<{ok: boolean, data?: {advertiserID: string, businessName: string}, message?: string, code?: string}>}
 */
export const startAddAdvertiserToAgency =
  (agencyID = "", businessName) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("addAdvertiserToAgency")}/addAdvertiserToAgency`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          agencyID,
          businessName: businessName?.trim() || "",
        },
      });

      if (data.ok) {
        const advertiserID = data?.data?.advertiserID;
        const advertiser = data?.data?.advertiser;

        if (advertiserID) {
          const advertiserPayload = {
            [advertiserID]: advertiser,
          };
          dispatch(getNextAdvertisers(advertiserPayload));
        }

        // Forzar refresh: el backend acaba de actualizar los custom claims
        // (advertisersAvailable) y sin force se reutiliza el token cacheado,
        // dejando Redux con la lista anterior.
        await dispatch(renewToken(true));

        return {
          ok: true,
          data: advertiser,
        };
      }
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startAddAdvertiserToAgency",
          context: { agencyID },
          tags: { operation: "create_advertiser_brand" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

/**
 * Elimina un advertiser (marca) de la agencia (RTDB, usuarios, marca borrada).
 * @param {string} advertiserID
 * @returns {Promise<{ ok: boolean, data?: { advertiserID: string }, message?: string, code?: string }>}
 */
export const startDeleteAdvertiserFromAgency =
  (advertiserID) => async (dispatch) => {
    try {
      const user = auth?.currentUser;
      if (!user) {
        return {
          ok: false,
          message: "User not authenticated",
          code: "UNAUTHORIZED",
        };
      }

      const token = await user.getIdToken();
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("deleteAdvertiserFromAgency")}/deleteAdvertiserFromAgency`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          advertiserID,
        },
      });

      if (data?.ok) {
        await dispatch(renewToken());
        return {
          ok: true,
          data: data?.data,
        };
      }

      return {
        ok: false,
        message: data?.message || "Failed to delete advertiser",
        code: data?.code,
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startDeleteAdvertiserFromAgency",
          context: { advertiserID },
          tags: { operation: "delete_advertiser_brand" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

export const startAddCreditsToAgency =
  (agencyID, credits) => async (dispatch, getState) => {
    try {
      const user = auth?.currentUser;
      if (!user) {
        return {
          ok: false,
          message: "User not authenticated",
          code: "UNAUTHORIZED",
        };
      }

      const token = await user.getIdToken();
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("addCreditsToAgency")}/addCreditsToAgency`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          agencyID,
          credits,
        },
      });

      if (data?.ok) {
        const newCredits = Number(data?.data?.newCredits || 0);
        const currentAdvertiser = getState()?.owner?.advertisers?.[agencyID];

        if (currentAdvertiser) {
          const advertiserPayload = {
            [agencyID]: {
              ...currentAdvertiser,
              subscriptionPlanInfo: {
                ...(currentAdvertiser.subscriptionPlanInfo || {}),
                usage: {
                  ...(currentAdvertiser.subscriptionPlanInfo?.usage || {}),
                  credits: newCredits,
                },
              },
            },
          };
          dispatch(getNextAdvertisers(advertiserPayload));
        }

        return {
          ok: true,
          data: data.data,
        };
      }

      return {
        ok: false,
        message: data?.message || "Failed to add credits",
        code: data?.code,
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(
          error,
          true,
          [MISSING_PARAMETERS, CREDITS_NOT_BE_NEGATIVE],
          {
            functionName: "startAddCreditsToAgency",
            context: { agencyID, credits },
            tags: { operation: "add_credits_to_advertiser" },
          }
        )
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

/**
 * Activa o desactiva el plan a nivel agencia (todos los advertisers de la agencia).
 * @param {string} agencyID
 * @returns {Promise<{ok: boolean, data?: object, message?: string, code?: string}>}
 */
export const startSwitchAgencyIsPlanActive =
  (agencyID = "") =>
  async (dispatch, getState) => {
    try {
      const user = auth?.currentUser;
      if (!user) {
        return {
          ok: false,
          message: "User not authenticated",
          code: "UNAUTHORIZED",
        };
      }

      const token = await user.getIdToken();
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "switchAgencyIsPlanActive"
        )}/switchAgencyIsPlanActive`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          agencyID,
        },
      });

      if (data?.ok) {
        const advertisers = getState()?.owner?.advertisers || {};

        const payload = {};
        Object.entries(advertisers).forEach(([advertiserID, adv]) => {
          if (adv?.agencyID !== agencyID) return;
          const toggled = !adv.isPlanActive;
          payload[advertiserID] = {
            ...adv,
            isPlanActive: toggled,
          };
        });

        if (Object.keys(payload).length > 0) {
          dispatch(getNextAdvertisers(payload));
        }

        return {
          ok: true,
          data: data.data,
        };
      }

      return {
        ok: false,
        message: data?.message || "Failed to switch plan status",
        code: data?.code,
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startSwitchAgencyIsPlanActive",
          context: { agencyID },
          tags: { operation: "switch_agency_plan_active" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

/**
 * Marca isDemo en RTDB para todos los advertisers de la agencia (advertisersByAgency).
 * @param {string} agencyID
 * @returns {Promise<{ok: boolean, message?: string, code?: string}>}
 */
export const startMarkAgencyAdvertisersAsDemo =
  (agencyID = "") =>
  async (dispatch, getState) => {
    try {
      if (!agencyID?.trim()) {
        return {
          ok: false,
          message: "Missing agencyID",
          code: "MISSING_PARAMETERS",
        };
      }

      const dbRef = ref(db, `advertisersByAgency/${agencyID}`);
      const snapshot = await get(dbRef);
      const raw = snapshot.val();
      const advertiserIDs = Array.isArray(raw) ? raw : [agencyID];

      if (advertiserIDs.length === 0) {
        return {
          ok: false,
          message: "No advertisers for agency",
          code: "EMPTY_AGENCY",
        };
      }

      const updates = {};

      advertiserIDs.forEach((id) => {
        updates[`advertisers/${id}/isDemo`] = true;
      });

      await update(ref(db), updates);

      const advertisers = getState()?.owner?.advertisers || {};
      const payload = {};

      advertiserIDs.forEach((advertiserID) => {
        const adv = advertisers[advertiserID];
        if (adv) {
          payload[advertiserID] = {
            ...adv,
            isDemo: true,
          };
        }
      });

      if (Object.keys(payload).length > 0) {
        dispatch(getNextAdvertisers(payload));
      }

      return { ok: true };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startMarkAgencyAdvertisersAsDemo",
          context: { agencyID },
          tags: { operation: "mark_agency_advertisers_demo" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

/**
 * Activa o desactiva un feature flag según su storage (advertiser / agency / agenciesFeatureFlags).
 */
export const startSetFeatureFlag =
  ({ key, enabled, advertiserID = "", agencyID = "" } = {}) =>
  async (dispatch, getState) => {
    try {
      const flag = getFeatureFlagByKey(key);
      if (!flag || typeof enabled !== "boolean") {
        return {
          ok: false,
          message: "Missing parameters",
          code: "MISSING_PARAMETERS",
        };
      }

      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("setFeatureFlag")}/setFeatureFlag`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          key,
          enabled,
          advertiserID,
          agencyID,
        },
      });

      if (!data?.ok) {
        return {
          ok: false,
          message: data?.message,
          code: data?.code,
        };
      }

      switch (flag.storage) {
        case FEATURE_FLAG_STORAGE.ADVERTISER: {
          if (!advertiserID) break;
          const current = getState()?.owner?.advertisers?.[advertiserID] || {};
          dispatch(
            getNextAdvertisers({
              [advertiserID]: {
                ...current,
                [flag.key]: enabled,
              },
            })
          );
          break;
        }
        case FEATURE_FLAG_STORAGE.AGENCY:
          break;
        case FEATURE_FLAG_STORAGE.AGENCIES_FEATURE_FLAGS: {
          if (!agencyID) break;
          const currentIds =
            getState()?.ui?.agenciesFeatureFlags?.[flag.key] ?? [];
          const nextIds = enabled
            ? currentIds.includes(agencyID)
              ? currentIds
              : [...currentIds, agencyID]
            : currentIds.filter((id) => id !== agencyID);
          dispatch(setAgenciesFeatureFlags({ [flag.key]: nextIds }));
          break;
        }
        default: {
          const _exhaustive = flag.storage;
          void _exhaustive;
          break;
        }
      }

      return { ok: true };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startSetFeatureFlag",
          context: { key, enabled, advertiserID, agencyID },
          tags: { operation: "set_feature_flag" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

/**
 * Sets the agency-level AI image provider via setFeatureFlag Cloud Function.
 */
export const startSetAgencyImageProvider =
  ({ agencyID, imageProvider } = {}) =>
  async (dispatch) => {
    try {
      if (!agencyID || !isValidAgencyImageProvider(imageProvider)) {
        return {
          ok: false,
          message: "Missing parameters",
          code: "MISSING_PARAMETERS",
        };
      }

      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("setFeatureFlag")}/setFeatureFlag`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          agencyID,
          imageProvider,
        },
      });

      if (!data?.ok) {
        return {
          ok: false,
          message: data?.message,
          code: data?.code,
        };
      }

      return { ok: true };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startSetAgencyImageProvider",
          context: { agencyID, imageProvider },
          tags: { operation: "set_agency_image_provider" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

/**
 * Crea un usuario owner en Firebase Auth con claim role: owner.
 * @param {{ name: string, email: string, password: string }} params
 * @returns {Promise<{ ok: boolean, data?: object, message?: string, code?: string }>}
 */
export const startCreateOwnerUser =
  ({ name, email, password }) =>
  async (dispatch) => {
    const run = async (isRetry = false) => {
      try {
        const token = await dispatch(renewToken(true));
        if (!token) {
          return {
            ok: false,
            message: "User not authenticated",
            code: "UNAUTHORIZED",
          };
        }

        const passwordEncrypted = encryptPassword(password);
        const { data } = await axios({
          method: "post",
          url: `${getCloudFunctionUrl("createOwnerUser")}/createOwnerUser`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            name,
            email,
            password: passwordEncrypted,
          },
        });

        if (data?.ok) {
          return {
            ok: true,
            data: data.data,
          };
        }

        return {
          ok: false,
          message: data?.message || "Failed to create owner user",
          code: data?.code,
        };
      } catch (error) {
        if (!isRetry && isNetworkError(error)) {
          return await run(true);
        }

        const errorFormatted = dispatch(
          LaunchError(error, true, [], {
            functionName: "startCreateOwnerUser",
            context: { email },
            tags: { operation: "create_owner_user" },
          })
        );

        return {
          ok: false,
          message: errorFormatted?.message,
          code: errorFormatted?.code,
        };
      }
    };

    return await run();
  };

/**
 * Envía correo a usuarios desde el dashboard de owner/admin.
 * @param {{ emails: string[], subject: string, body: string }} params
 * @returns {Promise<{ ok: boolean, message?: string, code?: string }>}
 */
export const startSendEmailFromOwner =
  ({ emails, subject, body }) =>
  async (dispatch) => {
    try {
      const user = auth?.currentUser;
      if (!user) {
        return {
          ok: false,
          message: "User not authenticated",
          code: "UNAUTHORIZED",
        };
      }

      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "POST",
        url: `${getCloudFunctionUrl("sendEmailFromOwner")}/sendEmailFromOwner`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          emails,
          subject,
          body,
        },
      });

      if (data?.ok) {
        return { ok: true, data: data.data };
      }

      return {
        ok: false,
        message: data?.message || "Failed to send email",
        code: data?.code,
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startSendEmailFromOwner",
          context: { emailsCount: emails?.length },
          tags: { operation: "send_email_from_owner" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

/**
 * Publica una notificación global y envía una campaña de Brevo por idioma.
 * @param {{ i18n: Record<string, { title: string, body: string, cta: string }>, link: string }} params
 * @returns {Promise<{ ok: boolean, data?: object, message?: string, code?: string }>}
 */
export const startPublishGlobalNotification =
  ({ i18n, link }) =>
  async (dispatch) => {
    try {
      const user = auth?.currentUser;
      if (!user) {
        return {
          ok: false,
          message: "User not authenticated",
          code: "UNAUTHORIZED",
        };
      }

      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "POST",
        url: `${getCloudFunctionUrl("publishGlobalNotification")}/publishGlobalNotification`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          i18n,
          link,
        },
      });

      if (data?.ok) {
        return { ok: true, data: data.data };
      }

      return {
        ok: false,
        message: data?.message || "Failed to publish global notification",
        code: data?.code,
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startPublishGlobalNotification",
          tags: { operation: "publish_global_notification" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

export const startGetReportedContentPaginated =
  ({ lastKey = null } = {}) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("reportedContent")}/reportedContent`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          lastKey,
        },
      });

      return {
        ok: true,
        data: data?.data || {
          items: [],
          hasMore: false,
          lastKey: null,
        },
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetReportedContentPaginated",
          context: { hasLastKey: !!lastKey },
          tags: { operation: "get_reported_content_paginated" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

export const startApproveReportedContent =
  ({ contentID } = {}) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "approveReportedContent"
        )}/approveReportedContent`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          contentID,
        },
      });

      if (data?.ok) {
        return { ok: true };
      }

      return {
        ok: false,
        message: data?.message,
        code: data?.code,
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startApproveReportedContent",
          context: { hasContentID: !!contentID },
          tags: { operation: "approve_reported_content" },
        })
      );

      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

export const startRejectReportedContent =
  ({ contentID } = {}) =>
  async (dispatch) => {
    try {
      await update(ref(db, `reportedContent/${contentID}`), {
        status: "rejected",
      });
      return { ok: true };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startRejectReportedContent",
          context: { hasContentID: !!contentID },
          tags: { operation: "reject_reported_content" },
        })
      );

      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

/**
 * Lista suscripciones de Stripe con paginación (Cloud Function).
 * Espera respuesta: { ok, data: { items, lastKey, hasMore } }.
 * @param {{ lastKey?: string | null }} params
 * @returns {Promise<{ ok: boolean, data?: { items: object[], lastKey: string | null, hasMore: boolean }, message?: string, code?: string }>}
 */
export const startGetSubscriptionsStripePaginated =
  ({ lastKey = null } = {}) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "getSubscriptionsStripePaginated"
        )}/getSubscriptionsStripePaginated`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          lastKey,
        },
      });

      return { ok: true, data: data.data };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetSubscriptionsStripePaginated",
          context: { hasLastKey: !!lastKey },
          tags: { operation: "get_subscriptions_stripe_paginated" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

/**
 * Creates a one-time login-as-client link for an agency billing root (solo or multi-brand).
 * @param {{ agencyID: string }} params
 * @returns {Promise<{ ok: boolean, data?: { hash: string }, message?: string, code?: string }>}
 */
export const startCreateOwnerLoginAsClientLink =
  ({ agencyID }) =>
  async (dispatch) => {
    const hasScope = Boolean(agencyID);
    try {
      const token = await dispatch(renewToken());
      if (!token) {
        return {
          ok: false,
          message: "User not authenticated",
          code: "unauthorized",
        };
      }

      return await dispatch(
        startRequestOwnerLoginAsClientLink({
          agencyID,
          token,
          url: `${getCloudFunctionUrl("createOwnerLoginAsClientLink")}/createOwnerLoginAsClientLink`,
          launchError: LaunchError,
        })
      );
    } catch (error) {
      const {
        error: safeError,
        showAlert,
        ignoreAlertByErrors,
        sentryMeta,
      } = buildOwnerLoginAsLaunchErrorArgs({
        code: typeof error?.code === "string" ? error.code : null,
        name: error?.name,
        cancelled: error?.cancelled === true,
        hasScope,
        message: "createOwnerLoginAsClientLink request failed",
      });
      const errorFormatted = await dispatch(
        LaunchError(safeError, showAlert, ignoreAlertByErrors, sentryMeta)
      );
      return {
        ok: false,
        message: errorFormatted?.message,
        code: errorFormatted?.code,
      };
    }
  };

/**
 * Exchanges a one-time login-as-client hash for a Firebase custom token.
 * @param {{ hash: string }} params
 * @returns {Promise<{ ok: boolean, data?: { customToken: string }, message?: string, code?: string }>}
 */
export const startExchangeOwnerLoginAsClientLink =
  ({ hash }) =>
  async (dispatch) => {
    try {
      const { data } = await axios({
        method: "POST",
        url: `${getCloudFunctionUrl("exchangeOwnerLoginAsClientLink")}/exchangeOwnerLoginAsClientLink`,
        data: {
          hash,
        },
      });

      if (data?.ok) {
        return {
          ok: true,
          data: data.data,
        };
      }

      return {
        ok: false,
        message: data?.message || "Failed to exchange login link",
        code: data?.code,
      };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startExchangeOwnerLoginAsClientLink",
          tags: { operation: "exchange_owner_login_as_client_link" },
        })
      );
      return {
        ok: false,
        message: errorFormatted.message,
        code: errorFormatted.code,
      };
    }
  };

// ---------------------------------------------------------------------------
// Promotional codes (owner RTDB `promotionalCodes`)
// ---------------------------------------------------------------------------

export const PROMOTIONAL_CODES_PATH = "promotionalCodes";

export const PROMOTIONAL_CODES_PAGE_SIZE = 10;
const PROMOTIONAL_CODES_PAGE_FETCH = PROMOTIONAL_CODES_PAGE_SIZE + 1;

export const PROMOTIONAL_CODE_TYPE_APP_SUMO = "appSumo";
export const PROMOTIONAL_CODE_TYPE_STANDARD = "standard";
export const PROMOTIONAL_CODE_TYPE_STRIPE_DISCOUNT = "stripeDiscount";

export const PROMOTIONAL_CODE_DISCOUNT_TYPE_FIXED = "fixed";
export const PROMOTIONAL_CODE_DISCOUNT_TYPE_PERCENTAGE = "percentage";

const normalizePromotionalCodeType = (data = {}) => {
  const raw = data?.type;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }
  return PROMOTIONAL_CODE_TYPE_STANDARD;
};

const normalizePromotionalCode = (raw) =>
  String(raw ?? "")
    .trim()
    .toUpperCase();

const getRegisteredUsersCount = (registeredUsers) => {
  if (registeredUsers == null) return 0;
  if (typeof registeredUsers === "number" && Number.isFinite(registeredUsers)) {
    return registeredUsers;
  }
  if (typeof registeredUsers === "object") {
    return Object.keys(registeredUsers).length;
  }
  return 0;
};

const promotionalCodeExists = async ({ code }) => {
  const normalized = normalizePromotionalCode(code);
  if (!normalized) return false;

  const dupQuery = query(
    ref(db, PROMOTIONAL_CODES_PATH),
    orderByChild("code"),
    equalTo(normalized),
    limitToFirst(1)
  );
  const snap = await get(dupQuery);
  return snap.exists();
};

export const fetchPromotionalCodesPage = async (cursorKey = null) => {
  const baseRef = ref(db, PROMOTIONAL_CODES_PATH);
  const hasCursor =
    cursorKey?.value != null && typeof cursorKey?.id === "string";
  const pageQuery = hasCursor
    ? query(
        baseRef,
        orderByChild("creationTimeNegative"),
        startAfter(cursorKey.value, cursorKey.id),
        limitToFirst(PROMOTIONAL_CODES_PAGE_FETCH)
      )
    : query(
        baseRef,
        orderByChild("creationTimeNegative"),
        limitToFirst(PROMOTIONAL_CODES_PAGE_FETCH)
      );

  const snap = await get(pageQuery);
  if (!snap.exists()) {
    return { rows: [], cursorAfter: null, hasMore: false };
  }

  const entries = [];
  snap.forEach((child) => {
    entries.push([child.key, child.val()]);
  });

  const hasMore = entries.length > PROMOTIONAL_CODES_PAGE_SIZE;
  const slice = hasMore
    ? entries.slice(0, PROMOTIONAL_CODES_PAGE_SIZE)
    : entries;

  const rows = slice.map(([id, data]) => {
    const creationTimeNegativeRaw = Number(data?.creationTimeNegative);
    const creationTimeNegative = Number.isNaN(creationTimeNegativeRaw)
      ? 0
      : creationTimeNegativeRaw;
    const isActive =
      data?.isActive === undefined ? true : Boolean(data?.isActive);
    return {
      id,
      code: String(data?.code ?? ""),
      creationTime: data?.creationTime ?? null,
      creationTimeNegative,
      registeredUsers: data?.registeredUsers,
      registeredUsersCount: getRegisteredUsersCount(data?.registeredUsers),
      type: normalizePromotionalCodeType(data),
      maxRedemptions: Number(data?.maxRedemptions) || 0,
      isActive,
      metadata:
        data?.metadata != null && typeof data.metadata === "object"
          ? data.metadata
          : null,
    };
  });

  const cursorAfter =
    rows.length > 0
      ? {
          value: rows[rows.length - 1].creationTimeNegative,
          id: rows[rows.length - 1].id,
        }
      : null;

  return { rows, cursorAfter, hasMore };
};

export const createPromotionalCode = async ({
  code,
  maxRedemptions,
  type,
  tier,
  stripeCode,
  discount,
  discountType,
}) => {
  const normalized = normalizePromotionalCode(code);
  if (!normalized) {
    throw new Error("INVALID_CODE");
  }

  const exists = await promotionalCodeExists({ code: normalized });
  if (exists) {
    throw new Error("DUPLICATE_CODE");
  }

  const now = Date.now();
  const max = Math.max(1, Math.floor(Number(maxRedemptions) || 1));
  let resolvedType = PROMOTIONAL_CODE_TYPE_STANDARD;
  if (type === PROMOTIONAL_CODE_TYPE_APP_SUMO) {
    resolvedType = PROMOTIONAL_CODE_TYPE_APP_SUMO;
  } else if (type === PROMOTIONAL_CODE_TYPE_STRIPE_DISCOUNT) {
    resolvedType = PROMOTIONAL_CODE_TYPE_STRIPE_DISCOUNT;
  }

  const payload = {
    creationTime: now,
    creationTimeNegative: -now,
    code: normalized,
    registeredUsers: 0,
    type: resolvedType,
    maxRedemptions: max,
    isActive: true,
  };

  if (resolvedType === PROMOTIONAL_CODE_TYPE_APP_SUMO) {
    const tierNum = Math.min(3, Math.max(1, Math.floor(Number(tier) || 1)));
    payload.metadata = { tier: tierNum };
  }

  if (resolvedType === PROMOTIONAL_CODE_TYPE_STRIPE_DISCOUNT) {
    const stripeCodeTrimmed = String(stripeCode ?? "").trim();
    if (!stripeCodeTrimmed) {
      throw new Error("INVALID_STRIPE_DISCOUNT");
    }
    const discountNum = Number(discount);
    if (!Number.isFinite(discountNum) || discountNum <= 0) {
      throw new Error("INVALID_STRIPE_DISCOUNT");
    }
    const resolvedDiscountType =
      discountType === PROMOTIONAL_CODE_DISCOUNT_TYPE_FIXED
        ? PROMOTIONAL_CODE_DISCOUNT_TYPE_FIXED
        : discountType === PROMOTIONAL_CODE_DISCOUNT_TYPE_PERCENTAGE
          ? PROMOTIONAL_CODE_DISCOUNT_TYPE_PERCENTAGE
          : null;
    if (!resolvedDiscountType) {
      throw new Error("INVALID_STRIPE_DISCOUNT");
    }
    if (
      resolvedDiscountType === PROMOTIONAL_CODE_DISCOUNT_TYPE_PERCENTAGE &&
      discountNum > 100
    ) {
      throw new Error("INVALID_STRIPE_DISCOUNT");
    }
    payload.metadata = {
      stripeCode: stripeCodeTrimmed,
      discount: discountNum,
      discountType: resolvedDiscountType,
    };
  }

  await push(ref(db, PROMOTIONAL_CODES_PATH), payload);
};

export const setPromotionalCodeIsActive = async ({ codeID, isActive }) => {
  if (!codeID) return;
  await update(ref(db, `${PROMOTIONAL_CODES_PATH}/${codeID}`), {
    isActive: Boolean(isActive),
  });
};
